/**
 * WHAT A CAPEX ADDRESS SHOWS WHILE PROJECT FINANCIALS IS SWITCHED OFF FOR THE COMPANY.
 *
 * App.jsx's `ModuleSwitchRoute` holds `/capex/*` on the server's switch, and an address it holds used
 * to land on the portal with nothing said. For most of Capex that is fine: the tile and the tab are
 * hidden too, so nobody is sent there. A CAPITAL ESTIMATE IS DIFFERENT (review finding 17, the rest of
 * it). Everything that opens one leads to `/capex/estimates/:id`: Bid Workup's redirect of a capital
 * bid, the bell and the emails (both go to the bid, which redirects), ProjectDetail's "View Bid", and
 * the old `/capital/estimates/:id` links. With Project Financials off, each of those dropped the person
 * on the portal as if they had clicked nothing. So an estimate's address says where the estimate
 * opens, that it is switched off, who can switch it on and where, and gives a way back. Every other
 * Capex address still goes to the portal, as it did.
 *
 * ONLY ON A REAL "OFF". ModuleSwitchRoute draws this page as `whenOff` only once the server has answered
 * for this company that Project Financials is not on. When the lookup itself failed (identity
 * restarting mid-deploy, a timeout, a body that is not JSON), nobody has said it is off, and saying so
 * sent an administrator to find it ON: App.jsx hands the route `<CapexSwitchedOff couldNotCheck />` as
 * `whenUnknown`, which says this page could not check, and offers a reload.
 *
 * Nothing here reaches the vendored module or its service (which refuses every call while the switch
 * is off), so this file is safe to load eagerly from App.jsx.
 */
import { Link, Navigate, matchPath, useLocation } from 'react-router-dom'

import AppHeader from '../../shared/components/AppHeader.jsx'
import { useAuth } from '../../hooks/useAuth.jsx'
import { useModuleSwitches } from '../../contexts/ModuleSwitchContext.jsx'
import { useT } from '../../shared/i18n/LanguageContext.jsx'

/** Every page of one estimate: its Overview and each step under it (CapexModule's estimate routes). */
export const ESTIMATE_ADDRESS = '/capex/estimates/:bidId/*'

export default function CapexSwitchedOff({ couldNotCheck = false }) {
  const { pathname } = useLocation()
  const estimate = matchPath({ path: ESTIMATE_ADDRESS }, pathname)
  if (!estimate) return <Navigate to="/portal" replace />
  return couldNotCheck
    ? <EstimateSwitchNotChecked bidId={estimate.params.bidId} />
    : <EstimateNeedsProjectFinancials bidId={estimate.params.bidId} />
}

/** The switches could not be read: say that, never "switched off", and offer to try again. */
function EstimateSwitchNotChecked({ bidId }) {
  const { user } = useAuth()
  const tm = useT('modules')
  const name = tm('capex') || 'Project Financials'
  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader title={name} user={user} />
      <main className="max-w-xl mx-auto px-4 py-12">
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-3" data-testid="estimate-switch-not-checked">
          <h1 className="text-xl font-semibold text-gray-900">This estimate opens in {name}</h1>
          <p className="text-gray-700">
            Estimate {bidId} is a capital estimate, which opens in {name}. This page could not check whether
            {' '}{name} is switched on for this company, so it has not opened it.
          </p>
          <p className="text-gray-700">Reload the page to check again.</p>
          <p className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
            >
              Reload
            </button>
            <Link to="/portal" className="text-blue-600 hover:text-blue-700 font-medium">Back to the portal</Link>
          </p>
        </div>
      </main>
    </div>
  )
}

function EstimateNeedsProjectFinancials({ bidId }) {
  const { user } = useAuth()
  const tm = useT('modules')
  const { switches } = useModuleSwitches()
  // Its capital estimates also need Capital Projects on (TenantModules says the same where the
  // switches are ticked), so an administrator who switched on only this one would see nothing change.
  const capitalOff = switches?.capital !== true
  const name = tm('capex') || 'Project Financials'
  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader title={name} user={user} />
      <main className="max-w-xl mx-auto px-4 py-12">
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-3" data-testid="estimate-needs-capex">
          <h1 className="text-xl font-semibold text-gray-900">This estimate opens in {name}</h1>
          <p className="text-gray-700">
            Estimate {bidId} is a capital estimate. Capital estimates are built, priced and awarded in {name},
            which is switched off for this company, so it cannot be opened.
          </p>
          <p className="text-gray-700">
            A platform administrator can switch {name} on for this company under the company&apos;s Modules.
            {capitalOff ? ' Capital estimates also need Capital Projects switched on there, and that is off too.' : ''}
          </p>
          <p>
            <Link to="/portal" className="text-blue-600 hover:text-blue-700 font-medium">Back to the portal</Link>
          </p>
        </div>
      </main>
    </div>
  )
}
